import { useState, useEffect } from 'react'
import { VideoGrid } from '@/components/VideoGrid'
import { Header } from '@/components/Header'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ErrorMessage } from '@/components/ErrorMessage'

interface Video {
  id: string
  title: string
  thumbnail: string
  channel_title: string
  channel_id: string
  published_at: string
  duration: string
  description?: string
}

export function HomePage() {
  const [videos, setVideos] = useState<Video[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const fetchVideos = async (query: string = '') => {
    setLoading(true)
    setError(null)
    
    try {
      const endpoint = query 
        ? `/.netlify/functions/search-videos?q=${encodeURIComponent(query)}`
        : '/.netlify/functions/get-videos'
      
      const response = await fetch(endpoint)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      setVideos(data.videos || [])
    } catch (err) {
      setError('Sorry! We couldn\'t load the videos. Please try again later.')
      console.error('Error fetching videos:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVideos()
  }, [])

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    fetchVideos(query)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <Header onSearch={handleSearch} />
      
      <main className="container mx-auto px-4 py-8">
        {loading && <LoadingSpinner />}
        
        {error && (
          <ErrorMessage 
            message={error} 
            onRetry={() => fetchVideos(searchQuery)} 
          />
        )}
        
        {!loading && !error && (
          <>
            {searchQuery && (
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Search results for: "{searchQuery}"
                </h2>
                <button
                  onClick={() => handleSearch('')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  ← Back to all videos
                </button>
              </div>
            )}
            
            <VideoGrid videos={videos} />
            
            {videos.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎬</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  No videos found
                </h3>
                <p className="text-gray-600">
                  {searchQuery 
                    ? "Try searching for something else!" 
                    : "Ask your parent to add some channels to watch!"
                  }
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}